import { IPetInsuranceFormData } from "@/api_framework/api_modals/PetInsurance";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import TableSkeleton from "@/app/components/ui/Skeletons/TableSkeleton";
import { usePetInsuranceFormsListAPI } from "@/app/hooks/api_hooks/PetInsurance/usePetInsuranceFormsListAPI";
import { usePetInsuranceSendFormAPI } from "@/app/hooks/api_hooks/PetInsurance/usePetInsuranceSendFormAPI";
import { useUtils } from "@/app/hooks/useUtils";
import React, { useEffect, useState } from "react";

interface IPetInsuranceFormsProps {
  customerId: string;
}

const PetInsuranceForms: React.FC<IPetInsuranceFormsProps> = ({ customerId }) => {
  const fetchPetInsuranceForms = usePetInsuranceFormsListAPI();
  const sendPetInsuranceForm = usePetInsuranceSendFormAPI();
  const { InvoicestatusBackgroundColor, veticDateFormat, InvoiceStatusDataTransformed } = useUtils();

  const [insuranceForms, setInsuranceForms] = useState<IPetInsuranceFormData[]>([]);
  const [isInsuranceLoading, setIsInsuranceLoading] = useState(true);
  const [sendingFormId, setSendingFormId] = useState<string | null>(null);
  const [mobileValues, setMobileValues] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!customerId) return;
    let mounted = true;
    setIsInsuranceLoading(true);
    fetchPetInsuranceForms(customerId)
      .then((rows) => {
        if (mounted) {
          setInsuranceForms(rows);
          setMobileValues({});
        }
      })
      .finally(() => {
        if (mounted) setIsInsuranceLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [customerId, fetchPetInsuranceForms]);

  const getMobileValue = (row: IPetInsuranceFormData) => mobileValues[row.form_id] ?? row.mobile;

  const handleMobileChange = (formId: string, value: string) =>
    setMobileValues((prev) => ({ ...prev, [formId]: value }));

  const handleSendInsuranceForm = async (row: IPetInsuranceFormData) => {
    setSendingFormId(row.form_id);
    await sendPetInsuranceForm(row.form_id, getMobileValue(row));
    setSendingFormId(null);
  };

  return (
    <div className="h-full pt-6">
      <div className="pb-0.5 rounded-lg bg-white px-2">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center pb-2 gap-2">
          <div>
            <h1 className="text-sm font-semibold leading-6 text-gray-900 mt-2">Pet Insurance Forms</h1>
            <p className="mt-1 text-xs text-gray-700">
              Send the pet insurance form to the customer. Only paid invoices are eligible.
            </p>
          </div>
        </div>
      </div>
      {isInsuranceLoading ? (
        <div className="mt-2">
          <TableSkeleton />
        </div>
      ) : insuranceForms?.length > 0 ? (
        <div className="mt-2 scroll-inner !h-auto max-h-[520px] sm:max-h-[650px] flow-root relative rounded-xl bg-[#F9FAFB] overflow-auto">
          <div className="w-full">
            <div className="inline-block min-w-full align-middle">
              <table className="w-full min-w-[720px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                <thead className="bg-[#F5F5F5] text-[#1F1F23] font-normal sticky top-0 z-20">
                  <tr>
                    {["Invoice Number", "Invoice Status", "Date", "Pet Name", "Mobile", "Send Form"].map((label) => (
                      <th
                        key={label}
                        scope="col"
                        className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black tracking-wider"
                      >
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {insuranceForms?.map((row, idx) => {
                    const isFirst = idx === 0;
                    const isLast = idx === insuranceForms.length - 1;
                    const isSingle = insuranceForms.length === 1;
                    const rowEnabled = !row?.has_active_refund;

                    return (
                      <tr
                        key={`pet-insurance-form:${row?.invoice_number}:${idx}`}
                        className={`
                          bg-white
                          ${isSingle ? "rounded-xl" : ""}
                          ${isFirst && !isSingle ? "rounded-t-xl" : ""}
                          ${isLast && !isSingle ? "rounded-b-xl" : ""}
                        `}
                      >
                        <td
                          className={`
                            whitespace-nowrap px-2 py-3 text-xs text-black
                            ${isSingle ? "rounded-tl-2xl rounded-bl-2xl" : ""}
                            ${isFirst && !isSingle ? "rounded-tl-2xl" : ""}
                            ${isLast && !isSingle ? "rounded-bl-2xl" : ""}
                          `}
                        >
                          {row?.invoice_number}
                        </td>
                        <td className="p-3 whitespace-nowrap text-xs font-normal" >
                          <button
                            className={`${InvoicestatusBackgroundColor(row?.invoice_status?.toLowerCase())} text-xs py-2 min-w-[120px] px-3 rounded-full flex items-center justify-center text-black`}
                          >
                            {InvoiceStatusDataTransformed(row?.invoice_status)}
                          </button>
                        </td>
                        <td className="whitespace-nowrap px-2 py-3 text-xs text-black">{veticDateFormat(row?.date)}</td>
                        <td className="whitespace-nowrap px-2 py-3 text-xs text-black">{row?.pet_name}</td>
                        <td className="whitespace-nowrap px-2 py-3 text-xs text-black">
                          <input
                            type="text"
                            value={getMobileValue(row)}
                            onChange={(e) => handleMobileChange(row.form_id, e.target.value)}
                            disabled={!rowEnabled}
                            className="w-full max-w-[160px] rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-black focus:outline-none focus:ring-2 focus:ring-[#1B68D3] disabled:bg-gray-100 disabled:cursor-not-allowed"
                          />
                        </td>
                        <td
                          className={`
                            whitespace-nowrap px-2 py-3 text-xs text-black
                            ${isSingle ? "rounded-tr-2xl rounded-br-2xl" : ""}
                            ${isFirst && !isSingle ? "rounded-tr-2xl" : ""}
                            ${isLast && !isSingle ? "rounded-br-2xl" : ""}
                          `}
                        >
                          <button
                            className="bg-black px-6 py-2 border-transparent rounded-md text-xs sm:text-xs text-white cursor-pointer font-medium w-full sm:w-auto disabled:bg-gray-500 disabled:cursor-not-allowed"
                            disabled={!rowEnabled || sendingFormId === row.form_id}
                            onClick={() => handleSendInsuranceForm(row)}
                          >
                            {sendingFormId === row.form_id ? "Sending..." : "Send Form"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg px-2 py-3">
          <NoDataFoundComponent message="No Pet Insurance Forms Found" />
        </div>
      )}
    </div>
  );
};

export default PetInsuranceForms;
